import type { ActResult, RoomAction, RoomView } from "@shared/types";
import { useCallback, useSyncExternalStore } from "react";
import { toast } from "../../components/Toast";
import { useI18n } from "../../i18n";
import type { RoomClient } from "../../lib/room-client";

export function useRoomView(client: RoomClient | null): RoomView | null {
  return useSyncExternalStore(
    (fn) => (client ? client.subscribe(fn) : () => undefined),
    () => client?.view() ?? null,
    () => client?.view() ?? null,
  );
}

export function useRoomStatus(client: RoomClient | null) {
  return useSyncExternalStore(
    (fn) => (client ? client.subscribe(fn) : () => undefined),
    () => client?.status() ?? "connecting",
    () => client?.status() ?? "connecting",
  );
}

/** act() that shows the translated error as a toast. */
export function useAct(client: RoomClient | null) {
  const { d } = useI18n();
  return useCallback(
    async (action: RoomAction, quiet = false): Promise<ActResult> => {
      if (!client) return { ok: false, error: "not_found" };
      const r = await client.act(action);
      if (!r.ok && !quiet) toast((d.err as Record<string, string>)[r.error] ?? d.err.server, "bad");
      return r;
    },
    [client, d],
  );
}

/** Game action shortcut: g({ t: "vote", option }) */
export function useGameAct(client: RoomClient | null) {
  const act = useAct(client);
  return useCallback((a: Record<string, unknown>, quiet = false) => act({ t: "g", a }, quiet), [act]);
}

export function playerById(view: RoomView, id: string | null | undefined) {
  return id ? view.players.find((p) => p.id === id) ?? null : null;
}
